import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useApiUrl,
  useCustom,
  useCustomMutation,
  useList,
  useNotification,
  useOne,
  useTranslate,
  useUpdate,
} from "@refinedev/core";
import {
  Alert,
  Button,
  Collapse,
  Empty,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import { ArrowRightOutlined, ReloadOutlined, SaveOutlined } from "@ant-design/icons";

type ServiceProviderProductMappingModalProps = {
  open: boolean;
  serviceProviderId: number | string | null;
  onCancel: () => void;
  onSuccess?: () => void;
};

type ProductRow = {
  id: number;
  name: string;
  type: string;
  group: string;
  code: string;
};

type RemoteProduct = {
  id: number | string;
  name: string;
  code?: string;
  type?: string;
  group?: string | null;
};

type ProductMapping = {
  productId: number;
  remoteProductId: string;
};

type AutoMapProductsResponse = {
  productMappings?: ProductMapping[];
};

const UNGROUPED_KEY = "__ungrouped__";
const EMPTY_PRODUCTS: never[] = [];

function productTypeTagColor(type: string) {
  if (type === "base") return "blue";
  if (type === "option") return "green";
  return "default";
}

export const ServiceProviderProductMappingModal: React.FC<
  ServiceProviderProductMappingModalProps
> = ({ open, serviceProviderId, onCancel, onSuccess }) => {
  const _t = useTranslate();
  const apiUrl = useApiUrl();
  const { open: openNotification } = useNotification();
  const [remoteByProductId, setRemoteByProductId] = useState<Record<number, string>>({});
  const [nameFilter, setNameFilter] = useState("");
  const initializedRef = useRef(false);
  const wasOpenRef = useRef(false);

  const {
    data: providerData,
    isLoading: providerLoading,
    refetch: refetchProvider,
  } = useOne({
    resource: "service-providers",
    id: serviceProviderId ?? "",
    queryOptions: {
      enabled: open && !!serviceProviderId,
    },
  });

  const {
    data: productsListData,
    isLoading: productsLoading,
    refetch: refetchProducts,
  } = useList({
    resource: "products",
    pagination: { mode: "off" },
    queryOptions: {
      enabled: open,
    },
  });

  const {
    data: remoteProductsResponse,
    isFetching: remoteCatalogFetching,
    isError: isRemoteCatalogError,
    refetch: refetchRemoteCatalog,
  } = useCustom<RemoteProduct[]>({
    url: serviceProviderId
      ? `${apiUrl}/service-providers/${serviceProviderId}/remote-products`
      : "",
    method: "get",
    errorNotification: false,
    queryOptions: {
      enabled: open && !!serviceProviderId,
      retry: false,
      queryKey: ["service-provider-remote-products", serviceProviderId],
    },
  });

  const record = providerData?.data;
  const products = productsListData?.data ?? EMPTY_PRODUCTS;
  const remoteProducts = remoteProductsResponse?.data ?? [];

  const sortedRows: ProductRow[] = useMemo(() => {
    return [...products]
      .map((p) => {
        const id = Number(p.id);
        return {
          id,
          name: typeof p.name === "string" ? p.name : "",
          type: typeof p.type === "string" ? p.type : "",
          group: typeof p.group === "string" ? p.group.trim() : "",
          code: typeof p.code === "string" ? p.code : "",
        };
      })
      .filter((row) => Number.isFinite(row.id))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  }, [products]);

  // Remote product catalog options for the searchable selects, grouped by
  // group + type (antd only supports a single OptGroup level, so the two
  // dimensions are combined into one header, e.g. "EGR family · Base").
  const remoteOptions = useMemo(() => {
    type Leaf = {
      value: string;
      label: string;
      name: string;
      type: string;
      group: string;
    };
    const buckets = new Map<string, { group: string; type: string; leaves: Leaf[] }>();
    for (const remote of remoteProducts) {
      const group = typeof remote.group === "string" ? remote.group.trim() : "";
      const type = typeof remote.type === "string" ? remote.type : "";
      const leaf: Leaf = {
        value: String(remote.id),
        label: remote.name || String(remote.id),
        name: remote.name || "",
        type,
        group,
      };
      const key = `${group || UNGROUPED_KEY}||${type}`;
      const bucket = buckets.get(key);
      if (bucket) bucket.leaves.push(leaf);
      else buckets.set(key, { group, type, leaves: [leaf] });
    }

    const typeOrder = (type: string) => (type === "base" ? 0 : type === "option" ? 1 : 2);
    const groupLabel = (group: string) =>
      group || _t("service-providers.productMapping.ungrouped");
    const typeLabel = (type: string) =>
      type ? _t(`products.fields.typeValues.${type}`) : "";

    return [...buckets.values()]
      .sort((a, b) => {
        // Ungrouped products sort last.
        const ag = a.group || "￿";
        const bg = b.group || "￿";
        const g = ag.localeCompare(bg, undefined, { sensitivity: "base" });
        if (g !== 0) return g;
        return typeOrder(a.type) - typeOrder(b.type);
      })
      .map((bucket) => ({
        label: typeLabel(bucket.type)
          ? `${groupLabel(bucket.group)} · ${typeLabel(bucket.type)}`
          : groupLabel(bucket.group),
        title: groupLabel(bucket.group),
        options: bucket.leaves.sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
        ),
      }));
  }, [remoteProducts, _t]);

  const filteredRows = useMemo(() => {
    const needle = nameFilter.trim().toLowerCase();
    if (!needle) return sortedRows;
    return sortedRows.filter((row) => row.name.toLowerCase().includes(needle));
  }, [sortedRows, nameFilter]);

  // Groups -> products, ordered: named groups alphabetically, ungrouped last,
  // products within each group ordered alphabetically by name.
  const groupedRows = useMemo(() => {
    const buckets = new Map<string, ProductRow[]>();
    for (const row of filteredRows) {
      const key = row.group || UNGROUPED_KEY;
      const list = buckets.get(key);
      if (list) list.push(row);
      else buckets.set(key, [row]);
    }

    const orderedKeys = [...buckets.keys()].sort((a, b) => {
      if (a === UNGROUPED_KEY) return 1;
      if (b === UNGROUPED_KEY) return -1;
      return a.localeCompare(b, undefined, { sensitivity: "base" });
    });

    return orderedKeys.map((key) => ({
      key,
      label: key === UNGROUPED_KEY ? _t("service-providers.productMapping.ungrouped") : key,
      rows: [...(buckets.get(key) ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
      ),
    }));
  }, [filteredRows, _t]);

  useEffect(() => {
    if (!open) {
      if (wasOpenRef.current) {
        initializedRef.current = false;
        setRemoteByProductId({});
        setNameFilter("");
      }
      wasOpenRef.current = false;
      return;
    }
    wasOpenRef.current = true;
    if (initializedRef.current) return;
    if (!record || !sortedRows.length) return;

    const fromMappings = new Map<number, string>(
      (record.productMappings ?? []).map((m: { productId: number; remoteProductId: string }) => [
        Number(m.productId),
        String(m.remoteProductId ?? ""),
      ]),
    );

    const next: Record<number, string> = {};
    for (const row of sortedRows) {
      next[row.id] = fromMappings.get(row.id) ?? "";
    }
    setRemoteByProductId(next);
    initializedRef.current = true;
  }, [open, record, sortedRows]);

  const { mutate: update, isLoading: isUpdating } = useUpdate();
  const { mutate: autoMapProducts, isLoading: isAutoMapping } = useCustomMutation();

  const applyMappingsToState = useCallback(
    (mappings: ProductMapping[]) => {
      const fromMappings = new Map<number, string>(
        mappings.map((m) => [Number(m.productId), String(m.remoteProductId ?? "")]),
      );
      setRemoteByProductId((prev) => {
        const next: Record<number, string> = {};
        for (const row of sortedRows) {
          next[row.id] = fromMappings.get(row.id) ?? "";
        }
        for (const [key, value] of Object.entries(prev)) {
          const numericKey = Number(key);
          if (!Number.isNaN(numericKey) && next[numericKey] === undefined) {
            next[numericKey] = value;
          }
        }
        return next;
      });
    },
    [sortedRows],
  );

  const setRemote = useCallback((productId: number, value: string) => {
    setRemoteByProductId((prev) => ({ ...prev, [productId]: value }));
  }, []);

  const handleCopyAllLocalIdsToRemote = useCallback(() => {
    setRemoteByProductId((prev) => {
      const next = { ...prev };
      for (const row of sortedRows) {
        next[row.id] = String(row.id);
      }
      return next;
    });
  }, [sortedRows]);

  const handleAutoMatchProducts = useCallback(() => {
    if (!serviceProviderId || isAutoMapping) return;

    autoMapProducts(
      {
        url: `${apiUrl}/service-providers/${serviceProviderId}/auto-map-products`,
        method: "post",
        values: {},
      },
      {
        onSuccess: (response) => {
          const parsed = (response as {
            data?: AutoMapProductsResponse & { statusCode?: number; status?: number };
          })?.data;
          const payload = (parsed ?? response) as AutoMapProductsResponse & {
            statusCode?: number;
            status?: number;
          };

          if (payload.statusCode === 502 || payload.status === 502) {
            openNotification?.({
              type: "error",
              message: _t("service-providers.productMapping.notifications.providerApiUnavailable"),
            });
            return;
          }

          const mappings = payload.productMappings ?? [];
          applyMappingsToState(mappings);

          void refetchProvider();
          void refetchProducts();
          onSuccess?.();

          openNotification?.({
            type: "success",
            message: _t("service-providers.productMapping.notifications.autoMapSuccess"),
          });
        },
        onError: (error: any) => {
          const isServiceProviderApiUnavailable =
            error?.statusCode === 502 ||
            error?.status === 502 ||
            error?.response?.data?.statusCode === 502;

          openNotification?.({
            type: "error",
            message: isServiceProviderApiUnavailable
              ? _t("service-providers.productMapping.notifications.providerApiUnavailable")
              : _t("service-providers.productMapping.notifications.autoMapError"),
          });
        },
      },
    );
  }, [
    apiUrl,
    applyMappingsToState,
    autoMapProducts,
    isAutoMapping,
    onSuccess,
    openNotification,
    refetchProducts,
    refetchProvider,
    serviceProviderId,
    _t,
  ]);

  const handleSave = () => {
    if (!serviceProviderId) return;

    const productMappings = Object.entries(remoteByProductId)
      .filter(([, remote]) => remote.trim().length > 0)
      .map(([productId, remoteProductId]) => ({
        productId: Number(productId),
        remoteProductId: remoteProductId.trim(),
      }));

    update(
      {
        resource: "service-providers",
        id: serviceProviderId,
        values: { productMappings },
      },
      {
        onSuccess: () => {
          initializedRef.current = false;
          onCancel();
          onSuccess?.();
        },
      },
    );
  };

  const loading = providerLoading || productsLoading || isAutoMapping;
  const frozen = isAutoMapping;
  const mappingDisabled = frozen || isRemoteCatalogError || remoteCatalogFetching;
  const totalMapped = useMemo(
    () => sortedRows.filter((row) => (remoteByProductId[row.id] ?? "").trim().length > 0).length,
    [sortedRows, remoteByProductId],
  );

  const renderRemoteSelect = useCallback(
    (row: ProductRow) => (
      <Select
        showSearch
        allowClear
        className="w-full font-mono"
        placeholder={_t("service-providers.productMapping.selectPlaceholder")}
        value={remoteByProductId[row.id] ? remoteByProductId[row.id] : undefined}
        options={remoteOptions}
        loading={remoteCatalogFetching}
        disabled={mappingDisabled}
        onChange={(value) => setRemote(row.id, value ?? "")}
        optionLabelProp="label"
        filterOption={(input, option) => {
          const needle = input.toLowerCase();
          const opt = option as unknown as {
            name?: string;
            group?: string;
            type?: string;
            value?: string;
          };
          return (
            (opt?.name ?? "").toLowerCase().includes(needle) ||
            (opt?.group ?? "").toLowerCase().includes(needle) ||
            (opt?.type ?? "").toLowerCase().includes(needle) ||
            (opt?.value ?? "").toLowerCase().includes(needle)
          );
        }}
        optionRender={(option) => {
          const data = option.data as unknown as { value: string; name: string };
          return (
            <span>
              <span className="font-mono text-neutral-400">#{data.value}</span> {data.name}
            </span>
          );
        }}
      />
    ),
    [_t, mappingDisabled, remoteByProductId, remoteCatalogFetching, remoteOptions, setRemote],
  );

  const collapseItems = useMemo(
    () =>
      groupedRows.map((group) => {
        const mappedInGroup = group.rows.filter(
          (row) => (remoteByProductId[row.id] ?? "").trim().length > 0,
        ).length;
        return {
          key: group.key,
          label: (
            <div className="flex items-center justify-between gap-2 pr-2">
              <span className="font-semibold">{group.label}</span>
              <Tag className="m-0" color={mappedInGroup === group.rows.length ? "green" : "default"}>
                {_t("service-providers.productMapping.mappedCount", {
                  mapped: mappedInGroup,
                  total: group.rows.length,
                })}
              </Tag>
            </div>
          ),
          children: (
            <div className="flex flex-col divide-y divide-neutral-100 dark:divide-neutral-800">
              {group.rows.map((row) => (
                <div key={row.id} className="flex items-center gap-3 py-2">
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <Typography.Text className="truncate text-sm">
                      <span className="font-mono text-neutral-500 dark:text-neutral-400">
                        #{row.id}
                      </span>
                      {" — "}
                      <span className="font-medium">{row.name || "—"}</span>
                    </Typography.Text>
                    {row.type ? (
                      <Tag color={productTypeTagColor(row.type)} className="m-0">
                        {_t(`products.fields.typeValues.${row.type}`)}
                      </Tag>
                    ) : null}
                  </div>
                  <Button
                    type="text"
                    size="small"
                    className="text-neutral-500"
                    icon={<ArrowRightOutlined />}
                    title={_t("service-providers.productMapping.copyIdToRemote")}
                    aria-label={_t("service-providers.productMapping.copyIdToRemote")}
                    disabled={mappingDisabled}
                    onClick={() => setRemote(row.id, String(row.id))}
                  />
                  <div className="w-[320px] max-w-[45%] shrink-0">{renderRemoteSelect(row)}</div>
                </div>
              ))}
            </div>
          ),
        };
      }),
    [groupedRows, remoteByProductId, _t, mappingDisabled, renderRemoteSelect, setRemote],
  );

  const defaultActiveKeys = useMemo(() => groupedRows.map((g) => g.key), [groupedRows]);

  return (
    <Modal
      title={_t("service-providers.titles.productMapping")}
      open={open}
      onCancel={() => {
        if (frozen) return;
        onCancel();
      }}
      width={900}
      destroyOnClose
      maskClosable={!frozen}
      keyboard={!frozen}
      closable={!frozen}
      className="service-provider-product-mapping-modal"
      styles={{ body: { paddingTop: 12 } }}
      footer={
        <Space>
          <Button onClick={onCancel} disabled={frozen}>
            {_t("buttons.cancel")}
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={isUpdating}
            disabled={!sortedRows.length || mappingDisabled}
            onClick={handleSave}
          >
            {_t("buttons.save")}
          </Button>
        </Space>
      }
    >
      <Spin spinning={loading}>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <Typography.Text type="secondary" className="text-sm">
            {_t("service-providers.productMapping.mappedCount", {
              mapped: totalMapped,
              total: sortedRows.length,
            })}
          </Typography.Text>
          <Space wrap>
            <Button
              type="default"
              icon={<ArrowRightOutlined />}
              disabled={!sortedRows.length || mappingDisabled}
              onClick={handleCopyAllLocalIdsToRemote}
            >
              {_t("service-providers.productMapping.copyAllToRemote")}
            </Button>
            <Button
              type="default"
              icon={<ArrowRightOutlined />}
              loading={isAutoMapping}
              disabled={!serviceProviderId || mappingDisabled}
              onClick={handleAutoMatchProducts}
            >
              {_t("service-providers.productMapping.autoMap")}
            </Button>
          </Space>
        </div>

        {sortedRows.length > 0 ? (
          <Input.Search
            allowClear
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            placeholder={_t("service-providers.productMapping.filterByName")}
            className="mb-3"
            disabled={frozen}
          />
        ) : null}

        {isRemoteCatalogError ? (
          <Alert
            type="error"
            showIcon
            className="mb-3"
            message={_t("service-providers.productMapping.notifications.providerApiUnavailable")}
            description={_t("service-providers.productMapping.remoteCatalogError")}
            action={
              <Button
                size="small"
                icon={<ReloadOutlined />}
                loading={remoteCatalogFetching}
                onClick={() => void refetchRemoteCatalog()}
              >
                {_t("service-providers.productMapping.retry")}
              </Button>
            }
          />
        ) : null}

        {!loading && sortedRows.length === 0 ? (
          <Empty
            className="py-8"
            description={_t("service-providers.productMapping.emptyProducts")}
          />
        ) : !loading && sortedRows.length > 0 && collapseItems.length === 0 ? (
          <Empty
            className="py-8"
            description={_t("service-providers.productMapping.emptyFilter")}
          />
        ) : collapseItems.length > 0 ? (
          <Collapse
            className="service-provider-product-mapping-collapse"
            defaultActiveKey={defaultActiveKeys}
            items={collapseItems}
          />
        ) : (
          <div className="py-12" />
        )}
      </Spin>
    </Modal>
  );
};
